// src/components/PromptInput.ts
import { LitElement, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { PromptChoice } from '../utils/diffsona-from-description';
import AppAnalytics from '../client/AnalyticsClient';
import { Portal } from '../utils/portal';

@customElement('prompt-input')
export class PromptInput extends LitElement {
  createRenderRoot() { return this; } // light DOM for Tailwind

  @property({ type: String }) label: string = '';
  @property({ type: Array }) values: PromptChoice[] = [];
  @property({ type: Boolean, attribute: 'allow-custom' }) allowCustom = false;
  @property({ type: Boolean }) disabled = false;
  @property({ type: String, attribute: 'prompting-tips' }) promptingTips: string = '';

  @property({ type: String }) selectedPrompt: string = '';
  @property({ type: String }) initialValue: string = '';
  @state() private lastInitialValue: string = '';

  @state() private expanded = false;
  @state() private focused = false;
  private blurTimer: number | null = null;
  private panelMouseDown = false;
  private isInitialized = false;

  @state() private valueOnOpen: string = '';
  private _inputId = `pi-${Math.random().toString(36).slice(2)}`;

  private thumbMap = new Map<string, string>();
  private portal: Portal | null = null;

  private choiceKey(v: PromptChoice) { return `${v.title}::${v.prompt}`; }
  private pickThumb(v: PromptChoice): string | null {
    const key = this.choiceKey(v);
    const cached = this.thumbMap.get(key);
    if (cached) return cached;

    const pool = v.images || []
    if (!pool.length) {
      return null;
    }

    const pickIx = Math.floor(Math.random() * (pool.length - 1))
    const pick = pool[pickIx];
    this.thumbMap.set(key, pick);
    return pick;
  }

  updated(changedProperties: Map<string, unknown>) {
    // React to external initialValue changes (e.g., from parent component prop updates)
    if (changedProperties.has('initialValue') && this.initialValue !== this.lastInitialValue) {
      this.lastInitialValue = this.initialValue;
      this.setValue(this.initialValue, /* isUserInteraction */ false);
      // Mark as initialized after first value load
      this.isInitialized = true;
    }
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.portal?.clear();
  }

  get value() {
    const input = this.querySelector<HTMLTextAreaElement>(`#${this._inputId}`);
    return (input?.value ?? this.selectedPrompt ?? '');
  }

  public setValue(v: string, isUserInteraction: boolean = true) {
    const previousValue = this.selectedPrompt;
    this.selectedPrompt = (v || '');
    const input = this.querySelector<HTMLTextAreaElement>(`#${this._inputId}`);
    if (input && input.value !== this.selectedPrompt) {
      input.value = this.selectedPrompt;
      this.autoResize(input);
    }
    this.requestUpdate();
    this.dispatchEvent(new CustomEvent('change', { detail: { value: this.selectedPrompt }, bubbles: true, composed: true }));

    // Track analytics only for user interactions (not initial value loads)
    if (isUserInteraction && this.isInitialized && previousValue !== this.selectedPrompt) {
      AppAnalytics.getPlausible()?.trackEvent('prompt_input_change', {
        props: {
          label: this.label || 'unknown',
          value: this.selectedPrompt.substring(0, 100), // Limit length for analytics
          value_length: this.selectedPrompt.length,
        },
      });
    }
  }

  private autoResize(textarea: HTMLTextAreaElement) {
    textarea.style.height = 'auto';
    textarea.style.height = textarea.scrollHeight + 'px';
  }

  private appendChunk(chunk: string) {
    const next = this.mergeChunks(this.value, chunk);
    this.setValue(next);
    const textarea = this.querySelector<HTMLTextAreaElement>(`#${this._inputId}`);
    textarea?.focus();
  }

  private mergeChunks(current: string, toAdd: string) {
    const clean = (s: string) => s.split(',').map(t => t.trim()).filter(Boolean);
    const list = new Set(clean(current));
    for (const a of clean(toAdd)) {
      let exists = false;
      for (const item of list) if (item.toLowerCase() === a.toLowerCase()) { exists = true; break; }
      if (!exists) list.add(a);
    }
    return Array.from(list).join(', ');
  }

  private onInput = (e: Event) => {
    const textarea = e.target as HTMLTextAreaElement;
    this.autoResize(textarea);
    this.setValue(textarea.value);
  };

  private clear(e?: Event) {
    e?.stopPropagation();
    this.setValue('');
    this.querySelector<HTMLTextAreaElement>(`#${this._inputId}`)?.focus();
  }

  private filtered(): PromptChoice[] {
    const q = this.value.toLowerCase();
    if (!q) return this.values;
    const f = this.values.filter(v =>
      v.title.toLowerCase().includes(q) || v.prompt.toLowerCase().includes(q)
    );
    return f.length ? f : this.values;
  }

  private onPanelMouseDown = () => {
    this.panelMouseDown = true;
  };
  private onPanelMouseUp = () => {
    this.panelMouseDown = false;
    this.querySelector<HTMLTextAreaElement>(`#${this._inputId}`)?.focus();
  };

  private expand() {
    if (this.disabled) return;
    this.expanded = true;
    this.valueOnOpen = this.value;
    this.thumbMap.clear();

    // Create portal for modal
    if (!this.portal) {
      this.portal = new Portal(`prompt-input-portal-${this._inputId}`);
    }

    // Render modal through portal
    this.renderModal();

    // Add a slight delay for the animation to start, then focus
    requestAnimationFrame(() => {
      setTimeout(() => {
        const textarea = document.querySelector<HTMLTextAreaElement>(`#prompt-input-portal-${this._inputId} #${this._inputId}`);
        if (textarea) {
          this.autoResize(textarea);
          textarea.focus();
          // Position cursor at the end for better UX
          textarea.setSelectionRange(textarea.value.length, textarea.value.length);
        }
      }, 150); // Small delay to let animations start
    });
  }

  private onFocus = () => {
    if (this.blurTimer) { window.clearTimeout(this.blurTimer); this.blurTimer = null; }
    this.focused = true; this.expanded = true;
  };
  private onBlur = () => {
    if (this.panelMouseDown) return;
    this.blurTimer = window.setTimeout(() => {
      this.focused = false; this.expanded = false; this.blurTimer = null;
      this.portal?.clear();
    }, 120);
  };

  private onKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault(); e.stopPropagation();
      // Add a subtle scale animation on escape
      const textarea = this.querySelector<HTMLTextAreaElement>(`#${this._inputId}`);
      textarea?.style.setProperty('transform', 'scale(0.98)');
      setTimeout(() => textarea?.style.removeProperty('transform'), 100);

      this.setValue(this.valueOnOpen || '');
      textarea?.blur();
      return;
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault(); e.stopPropagation();
      // Add a success animation on enter
      const textarea = this.querySelector<HTMLTextAreaElement>(`#${this._inputId}`);
      textarea?.style.setProperty('transform', 'scale(1.02)');
      setTimeout(() => textarea?.style.removeProperty('transform'), 100);

      textarea?.blur();
    }
    // Tab navigation enhancement
    if (e.key === 'Tab' && this.focused) {
      // Allow natural tab navigation within the modal
      const focusableElements = this.querySelectorAll('button, input');
      const firstElement = focusableElements[0] as HTMLElement;
      const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

      if (e.shiftKey && document.activeElement === firstElement) {
        e.preventDefault();
        lastElement?.focus();
      } else if (!e.shiftKey && document.activeElement === lastElement) {
        e.preventDefault();
        firstElement?.focus();
      }
    }
  };

  private renderModal() {
    if (!this.expanded || !this.portal) return;
    this.portal.render(this.getModalTemplate());
  }

  private getDefaultTips(): string {
    return `**Be Specific**: Instead of "beautiful woman", try "elegant woman with flowing auburn hair, wearing a silk evening gown"

**Front-Load Important Details**: Put the most important elements at the beginning for better emphasis

**Use Quality Boosters**: Terms like "realistic", "highly detailed", "professional photography"

**Describe Lighting and Mood**: "soft natural lighting", "dramatic shadows", "golden hour"

**Separate Concepts with Commas**: Use commas to separate elements: "red dress, beach sunset, wind in hair"

**Avoid Negative Language**: Instead of "not blurry", use "focused"

**Be Consistent**: Keep style consistent (e.g., don't mix "realistic" with "anime")`;
  }

  private getModalTemplate() {
    const filtered = this.filtered();
    const hasText = !!this.value;
    const tips = this.promptingTips || this.getDefaultTips();
    const tipsLabel = this.label ? `Prompting Tips` : 'Prompting Tips';

    return html`
      <div class="fixed right-[5%] top-[5%] w-[90vw] h-screen z-50">
        <div class="fixed inset-0 bg-gradient-to-br from-black/40 via-slate-900/50 to-black/60 backdrop-blur-md z-20
                    animate-in fade-in duration-300 ease-out"
             @click=${() => this.onBlur()}></div>

        <div class="space-y-3 relative z-30 animate-in slide-in-from-bottom-4 fade-in duration-400 ease-out
                    ${this.disabled ? 'opacity-70 pointer-events-none select-none' : ''}">
          ${this.label ? html`<div class="text-base sm:text-lg font-semibold text-slate-200 animate-in slide-in-from-top-2 fade-in duration-300 delay-100">${this.label}</div>` : null}

          <div class="relative animate-in slide-in-from-bottom-2 fade-in duration-300 delay-150">
            <textarea
              id=${this._inputId}
              rows="1"
              class="w-full rounded-2xl bg-slate-900/80 border border-white/20 px-6 py-4 pr-24
                     shadow-xl shadow-black/20 backdrop-blur-sm
                     focus:outline-none focus:ring-2 focus:ring-indigo-400/50 focus:border-indigo-400/30
                     transition-all duration-300 ease-out
                     text-slate-100 placeholder:text-slate-400
                     hover:bg-slate-900/90 hover:border-white/30
                     resize-none overflow-hidden"
              placeholder=${this.allowCustom ? 'Type to filter or enter custom…' : 'Pick from quickfill below'}
              ?readOnly=${!this.allowCustom}
              ?disabled=${this.disabled}
              @input=${this.onInput}
              @focus=${this.onFocus}
              @blur=${this.onBlur}
              @keydown=${this.onKeyDown}
              .value=${this.selectedPrompt}
              aria-label=${this.label || 'Prompt'}
              autocomplete="off"
            ></textarea>

            <div class="absolute right-3 top-1/2 -translate-y-1/2 flex gap-2 animate-in slide-in-from-right-2 fade-in duration-300 delay-200">
              <button
                type="button"
                class="cursor-pointer px-4 py-2 flex justify-center items-center rounded-xl border border-white/20 bg-white/10
                       hover:bg-indigo-500/30 hover:border-indigo-400/50 hover:scale-110 hover:rotate-12
                       text-slate-300 hover:text-indigo-200
                       transition-all duration-300 ease-out
                       active:scale-95 active:rotate-6"
                @click=${() => {
                  this.expanded = false;
                  this.onBlur();
                }}
                title="Save & Close"
              >Save</button>
            </div>

            <!-- Quickfill with optional thumbnails -->
            ${this.focused ? html`
              <div
                class="absolute z-30 mt-4 left-0 right-0 rounded-2xl border border-white/20 bg-slate-950/95 backdrop-blur-md
                       shadow-2xl shadow-black/40 overflow-hidden
                       animate-in slide-in-from-bottom-4 fade-in duration-400 ease-out delay-300"
                @mousedown=${this.onPanelMouseDown}
                @mouseup=${this.onPanelMouseUp}
              >
                <div class="px-4 py-3 text-xs uppercase tracking-wide text-slate-300 border-b border-white/20 bg-slate-900/50
                           animate-in slide-in-from-top-2 fade-in duration-300 delay-400">
                  <span class="flex items-center gap-2">
                    <span class="w-2 h-2 bg-indigo-400 rounded-full animate-pulse"></span>
                    Quickfill <span class="ml-1 text-slate-500 normal-case font-mono">(${filtered.length})</span>
                  </span>
                </div>

                <div class="max-h-80 overflow-auto p-3 flex flex-wrap gap-3">
                  ${filtered.map((v, index) => html`
                    <button
                      type="button"
                      class="flex items-center gap-3 rounded-xl border border-white/20 bg-white/[0.05]
                             hover:bg-white/15 hover:border-white/30 hover:scale-105 hover:shadow-lg hover:shadow-indigo-500/20
                             text-left relative min-w-40 min-h-40 group
                             transition-all duration-300 ease-out
                             active:scale-95 active:bg-white/20
                             animate-in slide-in-from-bottom-2 fade-in duration-300"
                      style="animation-delay: ${400 + index * 50}ms"
                      @click=${() => this.appendChunk(v.prompt)}
                      title=${v.prompt}
                    >
                      ${Boolean(v.images?.length)
        ? html`<img src=${this.pickThumb(v)} alt="" loading="lazy"
                     class="h-70 rounded-lg object-cover border border-white/20
                            group-hover:border-indigo-400/50 transition-all duration-300" />`
        : html`<div class="h-70 w-full rounded-lg bg-gradient-to-br from-slate-800 to-slate-900
                         border border-white/20 flex items-center justify-center">
                  <span class="text-2xl opacity-30">📝</span>
                </div>`}
                      <div class="absolute bg-gradient-to-t from-black/80 via-black/50 to-transparent
                                  rounded-b-xl p-3 bottom-0 w-full
                                  group-hover:from-indigo-900/80 transition-all duration-300">
                        <div class="text-xs font-medium text-slate-200 truncate group-hover:text-white">${v.title}</div>
                        <div class="text-[11px] text-slate-400 truncate group-hover:text-indigo-200">${v.prompt}</div>
                      </div>
                      <div class="absolute top-2 right-2 w-6 h-6 rounded-full bg-indigo-500/20 backdrop-blur-sm
                                  border border-indigo-400/30 flex items-center justify-center
                                  opacity-0 group-hover:opacity-100 transition-all duration-300 scale-75 group-hover:scale-100">
                        <span class="text-indigo-300 text-xs">+</span>
                      </div>
                    </button>
                  `)}
                </div>
              </div>
            ` : null}
          </div>

          <!-- Quick Fill Collapsible -->
          ${this.values.length > 0 ? html`
            <collapsible-section
              title="Quick Fill"
              icon="⚡"
              description="Add presets to your prompt"
              .open=${false}
              @mousedown=${this.onPanelMouseDown}
              @mouseup=${this.onPanelMouseUp}
            >
              <div class="flex flex-wrap gap-2 animate-in slide-in-from-bottom-2 fade-in duration-300">
                ${this.values.map((v, index) => html`
                  <button
                    type="button"
                    class="px-3 py-1.5 rounded-lg border border-white/20 bg-white/5
                           hover:bg-indigo-500/20 hover:border-indigo-400/40 hover:scale-105
                           text-xs text-slate-200 hover:text-indigo-200
                           transition-all duration-200 ease-out
                           active:scale-95"
                    style="animation-delay: ${index * 30}ms"
                    @click=${() => this.appendChunk(v.prompt)}
                    title=${v.prompt}
                  >
                    ${v.title}
                  </button>
                `)}
              </div>
            </collapsible-section>
          ` : null}

          <!-- Prompting Tips Collapsible -->
          <collapsible-section
            title="${tipsLabel}"
            icon="💡"
            description="How to prompt effectively"
            .open=${false}
            @mousedown=${this.onPanelMouseDown}
            @mouseup=${this.onPanelMouseUp}
          >
            <div class="prose prose-sm prose-invert max-w-none">
              <div class="text-slate-300 leading-relaxed space-y-2 text-xs">
                ${tips.split('\n\n').map(paragraph => {
                  // Check if paragraph starts with bold text (markdown **text**)
                  const boldMatch = paragraph.match(/^\*\*(.+?)\*\*:\s*(.+)$/);
                  if (boldMatch) {
                    return html`
                      <div>
                        <span class="font-semibold text-indigo-300">${boldMatch[1]}:</span>
                        <span class="text-slate-300"> ${boldMatch[2]}</span>
                      </div>
                    `;
                  }
                  return html`<p class="text-slate-300">${paragraph}</p>`;
                })}
              </div>
            </div>
          </collapsible-section>
        </div>
      </div>
    `;
  }

  render() {
    const hasText = !!this.value;
    const displayText = hasText
      ? (this.value.length > 50 ? this.value.substring(0, 50) + '…' : this.value)
      : (this.label ? `${this.label}…` : 'Select…');

    return html`
      <button
        type="button"
        class="w-full rounded-xl bg-slate-900/60 border border-white/10 px-4 py-2
               hover:bg-slate-900/80 hover:border-white/20 hover:scale-[1.02]
               transition-all duration-200 ease-out
               flex items-center justify-between gap-2
               ${this.disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
               active:scale-95"
        @click=${() => this.expand()}
        ?disabled=${this.disabled}
        title=${this.value || 'Click to edit'}
      >
        <span class="text-sm text-slate-200 truncate flex-1 text-left max-w-32 ${hasText ? '' : 'italic opacity-70'}">
          ${displayText}
        </span>
        <span class="text-lg flex-shrink-0">✏️</span>
      </button>
    `;
  }
}

